<template>
  <PeppolContainer size="extra-small" :loading="sending">
    <div class="flex flex-col gap-1.5 text-sm">
      <div
        v-if="invoice.peppol_sent_at"
        class="flex flex-col gap-1 rounded border bg-primary-50 px-1.5 py-1"
      >
        <span class="font-semibold">
          {{
            $t('global.peppol.sent_at', {
              date: $h.formatDate(invoice.peppol_sent_at, 'DD/MM/YYYY HH:mm')
            })
          }}
        </span>
        <span
          v-if="invoice.peppol_status"
          class="inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
          :class="statusClasses"
        >
          <FontAwesomeIcon :icon="['fas', statusIcon]" />
          {{ $t(`enums.peppol_invoice_status.${invoice.peppol_status}`) }}
        </span>
      </div>
      <p v-if="invoice.peppol_error" class="text-xs text-red-700">
        {{ invoice.peppol_error }}
      </p>
      <BaseButton
        v-if="canSend"
        :title="
          isRejected ? $t('button.peppol.resend') : $t('button.peppol.send')
        "
        size="extra-small"
        icon="paper-plane"
        color="primary"
        inverse-icon-order
        block
        :disabled="sending"
        @click="send"
      />
    </div>
  </PeppolContainer>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapActions } from 'vuex'

import PeppolContainer from '@c/Partner/Peppol/PeppolContainer.vue'

const STATUS_STYLES = {
  Pending: { classes: 'bg-amber-100 text-amber-800', icon: 'clock' },
  Ok: { classes: 'bg-green-100 text-green-800', icon: 'circle-check' },
  Error: { classes: 'bg-red-100 text-red-800', icon: 'times-circle' }
}

/**
 * An invoice's Peppol block, after FM's component of the same name: when it
 * was sent, the acknowledgement (Pending / Ok / Error, read with the invoice,
 * no polling), the rejection reason, and the send / resend button. The back
 * checks the rest and answers 422 {message}, toasted by apiClient.
 */
export default {
  name: 'PeppolSendInvoiceComponent',
  components: { FontAwesomeIcon, PeppolContainer },
  props: {
    invoice: { type: Object, required: true }
  },
  data() {
    return { sending: false }
  },
  computed: {
    isRejected() {
      return this.invoice.peppol_status === 'Error'
    },
    canSend() {
      return (
        this.$can('manage_invoicing') &&
        (!this.invoice.peppol_sent_at || this.isRejected)
      )
    },
    statusClasses() {
      return STATUS_STYLES[this.invoice.peppol_status]?.classes ?? ''
    },
    statusIcon() {
      return STATUS_STYLES[this.invoice.peppol_status]?.icon ?? 'clock'
    }
  },
  methods: {
    ...mapActions({ sendInvoiceViaPeppol: 'invoicing/sendInvoiceViaPeppol' }),
    send() {
      this.sending = true
      this.sendInvoiceViaPeppol({ id: this.invoice.id })
        .then(() => {
          this.$h.toastSuccess(this.$t('toasts.peppol.sent'))
        })
        .catch(() => {})
        .finally(() => {
          this.sending = false
        })
    }
  }
}
</script>
